import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, BookmarkPlus } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { FilterSidebar } from "@/components/FilterSidebar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "savings", label: "Biggest Saving" },
  { value: "best_condition", label: "Best Condition" },
  { value: "popular", label: "Most Popular" },
];

const GENDER_SHORT = { "Women's": "Womens", "Men's": "Mens", "Unisex": "Unisex" };
const slugifyChip = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const msUntilMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return Math.max(0, midnight - now);
};

function SaleCountdown() {
  const [remaining, setRemaining] = useState(msUntilMidnight());
  useEffect(() => {
    const t = setInterval(() => setRemaining(msUntilMidnight()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const hrs = Math.floor(remaining / 3_600_000);
  const mins = Math.floor((remaining % 3_600_000) / 60_000);
  const secs = Math.floor((remaining % 60_000) / 1000);
  return (
    <div className="inline-flex items-center gap-2 bg-rose-600 text-white text-xs font-heading font-bold uppercase px-3 py-2 mb-3" data-testid="sale-countdown-badge">
      <Clock size={14} /> Today's Deals End In {pad(hrs)}:{pad(mins)}:{pad(secs)}
    </div>
  );
}

export default function Shop() {
  const { user } = useAuth();
  const [fitReference, setFitReference] = useState(null);
  const [params, setParams] = useSearchParams();
  const [filters, setFiltersState] = useState({
    brand: params.get("brand") || "",
    shape: params.get("shape") || "",
    style: params.get("style") || "",
    gender: params.get("gender") || "",
    material: params.get("material") || "",
    colour: params.get("colour") || "",
    condition_min: params.get("condition_min") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    lens_width_min: params.get("lens_width_min") || "",
    lens_width_max: params.get("lens_width_max") || "",
    bridge_width_min: params.get("bridge_width_min") || "",
    bridge_width_max: params.get("bridge_width_max") || "",
    temple_length_min: params.get("temple_length_min") || "",
    temple_length_max: params.get("temple_length_max") || "",
    discount_min: params.get("discount_min") || "",
    search: params.get("search") || "",
  });
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [collection] = useState(params.get("collection") || "");
  const [result, setResult] = useState({ items: [], total: 0 });
  const [brands, setBrands] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [searchLabel, setSearchLabel] = useState("");

  const setFilters = (fn) => setFiltersState(fn);

  useEffect(() => {
    api.get("/brands").then((res) => setBrands(res.data));
    api.get("/filters/meta").then((res) => setMeta(res.data));
  }, []);

  useEffect(() => {
    if (!user) {
      setFitReference(null);
      return;
    }
    api.get("/account/measurements").then((res) => setFitReference(res.data[0] || null)).catch(() => {});
  }, [user]);

  const load = useCallback(() => {
    setLoading(true);
    const query = { ...filters, sort, limit: 24 };
    if (collection) query.collection = collection;
    Object.keys(query).forEach((k) => (!query[k] ? delete query[k] : null));

    const next = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => next.set(k, v));
    if (collection) next.set("collection", collection);
    setParams(next, { replace: true });

    api.get("/products", { params: query }).then((res) => setResult(res.data)).finally(() => setLoading(false));
  }, [filters, sort, collection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const genderList = (filters.gender || "").split(",").filter(Boolean);
  const pageTitle = collection
    ? collection.replace(/-/g, " ")
    : genderList.length
    ? genderList.map((g) => GENDER_SHORT[g] || g).join(" & ")
    : "Shop All Frames";

  const removeGender = (g) => {
    setFiltersState((prev) => ({ ...prev, gender: genderList.filter((x) => x !== g).join(",") }));
  };

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k !== "search" && v);

  const saveSearch = async () => {
    if (!searchLabel.trim()) return;
    try {
      const activeFilters = { ...filters };
      Object.keys(activeFilters).forEach((k) => (!activeFilters[k] ? delete activeFilters[k] : null));
      await api.post("/account/saved-searches", { label: searchLabel.trim(), filters: activeFilters });
      toast.success("Search saved — we'll email and notify you when a new match arrives");
      setSavingSearch(false);
      setSearchLabel("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const QUICK_FILTERS = [
    { label: "All", test: () => !filters.gender && !filters.condition_min && !filters.max_price && !filters.brand, apply: () => setFiltersState({ brand: "", shape: "", style: "", gender: "", condition_min: "", min_price: "", max_price: "", lens_width_min: "", lens_width_max: "", bridge_width_min: "", bridge_width_max: "", temple_length_min: "", temple_length_max: "", discount_min: "", search: "" }) },
    { label: "Men's", test: () => filters.gender === "Men's", apply: () => setFiltersState((p) => ({ ...p, gender: "Men's" })) },
    { label: "Women's", test: () => filters.gender === "Women's", apply: () => setFiltersState((p) => ({ ...p, gender: "Women's" })) },
    { label: "9-10 Mint", test: () => filters.condition_min === "9", apply: () => setFiltersState((p) => ({ ...p, condition_min: "9" })) },
    { label: "Under $150", test: () => filters.max_price === "150", apply: () => setFiltersState((p) => ({ ...p, max_price: "150" })) },
    ...brands.slice(0, 3).map((b) => ({ label: b.name, test: () => filters.brand === b.slug, apply: () => setFiltersState((p) => ({ ...p, brand: b.slug })) })),
  ];

  return (
    <div className="container-lookers py-10" data-testid="shop-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-2">
          {pageTitle}
        </h1>
        {collection === "sale" && <SaleCountdown />}
        <p className="text-sm text-neutral-500 mb-4 font-mono">{loading ? "Loading…" : `${result.total} frames`}</p>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="quick-pill-filters">
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={f.apply}
              className={`px-4 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-wide transition-colors ${f.test() ? "bg-charcoal text-white" : "bg-[#F8F8F9] text-neutral-600 hover:bg-neutral-200"}`}
              data-testid={`quick-pill-${slugifyChip(f.label)}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {genderList.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="active-filter-chips">
            {genderList.map((g) => (
              <button
                key={g}
                onClick={() => removeGender(g)}
                className="flex items-center gap-2 bg-teal-600 text-white text-xs font-heading font-bold uppercase px-3 py-1.5 rounded-full hover:bg-teal-700 transition-colors"
                data-testid={`active-filter-chip-gender-${slugifyChip(g)}`}
              >
                {GENDER_SHORT[g] || g} <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}

        {user && hasActiveFilters && (
          <div className="mt-4">
            {savingSearch ? (
              <div className="flex items-center gap-2" data-testid="save-search-form">
                <input
                  autoFocus
                  value={searchLabel}
                  onChange={(e) => setSearchLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveSearch()}
                  placeholder="Name this search (e.g. Round Ray-Bans under $200)"
                  className="border border-neutral-200 rounded-full px-4 py-2 text-xs focus:border-teal-600 outline-none w-72"
                  data-testid="save-search-label-input"
                />
                <button onClick={saveSearch} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-heading font-bold uppercase px-4 py-2 rounded-full transition-colors" data-testid="save-search-confirm-button">Save</button>
                <button onClick={() => setSavingSearch(false)} className="text-xs text-neutral-400 hover:text-neutral-600" data-testid="save-search-cancel-button">Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setSavingSearch(true)}
                className="flex items-center gap-1.5 text-xs font-heading font-bold uppercase text-teal-700 hover:text-teal-800 transition-colors"
                data-testid="save-search-button"
              >
                <BookmarkPlus size={14} /> Save This Search &amp; Get Notified
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-6 lg:hidden">
        <button onClick={() => setShowFilters(!showFilters)} className="border border-neutral-200 rounded-full px-5 py-2.5 text-xs font-heading font-bold uppercase text-neutral-700 hover:border-charcoal transition-colors" data-testid="mobile-filter-toggle">
          Filters
        </button>
        {collection === "sale" ? (
          <span className="text-xs font-heading font-bold uppercase text-teal-700" data-testid="sort-select-mobile">Sorted: Biggest Discount</span>
        ) : (
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-neutral-200 rounded-full bg-white text-neutral-700 px-4 py-2.5 text-xs focus:border-teal-600 outline-none" data-testid="sort-select-mobile">
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        <div className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <FilterSidebar filters={filters} setFilters={setFilters} brands={brands} meta={meta} />
        </div>
        <div className="flex-1 w-full min-w-0">
          <div className="hidden lg:flex justify-end mb-6">
            {collection === "sale" ? (
              <span className="text-xs font-heading font-bold uppercase text-teal-700 px-4 py-2.5" data-testid="sort-select">Sorted: Biggest Discount</span>
            ) : (
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-neutral-200 rounded-full bg-white text-neutral-700 px-4 py-2.5 text-xs focus:border-teal-600 outline-none" data-testid="sort-select">
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            )}
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-12" data-testid="shop-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-white rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : result.items.length === 0 ? (
            <div className="text-center py-24 text-neutral-400" data-testid="shop-empty-state">
              <p className="mb-2">No products match your filters.</p>
              <button onClick={() => setFiltersState({})} className="underline text-sm text-teal-600">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-16" data-testid="shop-results">
              {result.items.map((p) => (
                <ProductCard key={p.id} product={p} fitReference={fitReference} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
